/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/Sidebar.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                        |
 * |---|------------------------|-------------------|------------------|-------------------------------------|
 * | 1 | 2026-06-18 (Phase 3b)  | Claude Sonnet 4.6 | 4-tab nav        | Created — collapsible sidebar nav   |
 * | 2 | 2026-06-19 (Settings)  | Claude Sonnet 4.6 | Settings page    | Added Settings tab pinned at bottom |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<Sidebar />)
 *
 * PROPS / EXPORTS
 * ---------------
 * StudioTab       — union type for all tabs (record | testcases | execution | reports | settings)
 * SidebarProps    — activeTab + onTabChange
 * Sidebar         — the component
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Expanded/collapsed state is persisted to localStorage key "mimiq-sidebar-expanded".
 * 2. Collapsed mode shows icon-only (48px wide). Tooltips via title= attribute.
 * 3. Active tab has a left accent bar + highlighted background.
 * 4. Settings tab is pinned at the bottom of the nav, separated by a spacer.
 *
 * =============================================================================
 */

import { useState, useEffect } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export type StudioTab = "record" | "testcases" | "functions" | "execution" | "reports" | "wiki" | "settings";

export interface SidebarProps {
  activeTab: StudioTab;
  onTabChange: (tab: StudioTab) => void;
}

// ── Tab definitions ───────────────────────────────────────────────────────────

const MAIN_TABS: { id: StudioTab; label: string; icon: string }[] = [
  { id: "record",    label: "Record",     icon: "⏺" },
  { id: "testcases", label: "Test Cases", icon: "☰" },
  { id: "execution", label: "Execution",  icon: "▶" },
  { id: "reports",   label: "Reports",    icon: "📊" },
];

const BOTTOM_TABS: { id: StudioTab; label: string; icon: string }[] = [
  { id: "functions", label: "Functions",  icon: "♻" },
  { id: "wiki",      label: "Wiki / Docs", icon: "?" },
  { id: "settings",  label: "Settings",    icon: "⚙" },
];

const LS_KEY = "mimiq-sidebar-expanded";

// ── Component ─────────────────────────────────────────────────────────────────

export function Sidebar({ activeTab, onTabChange }: SidebarProps) {
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_KEY) !== "false"; } catch { return true; }
  });

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, String(expanded)); } catch { /* ignore */ }
  }, [expanded]);

  function renderTab({ id, label, icon }: { id: StudioTab; label: string; icon: string }) {
    return (
      <li key={id}>
        <button
          className={`sidebar-item${activeTab === id ? " sidebar-item--active" : ""}`}
          onClick={() => onTabChange(id)}
          title={expanded ? undefined : label}
          aria-current={activeTab === id ? "page" : undefined}
        >
          <span className="sidebar-item-icon">{icon}</span>
          {expanded && <span className="sidebar-item-label">{label}</span>}
        </button>
      </li>
    );
  }

  return (
    <nav className={`sidebar${expanded ? " sidebar--expanded" : ""}`} aria-label="Main navigation">
      <div className="sidebar-logo">
        {expanded ? <span className="sidebar-logo-text">Mimiq</span> : <span className="sidebar-logo-icon">M</span>}
      </div>

      <ul className="sidebar-nav" role="list">
        {MAIN_TABS.map(renderTab)}
      </ul>

      {/* spacer pushes bottom tabs to the bottom */}
      <div className="sidebar-spacer" />

      <ul className="sidebar-nav sidebar-nav--bottom" role="list">
        {BOTTOM_TABS.map(renderTab)}
      </ul>

      <button
        className="sidebar-toggle"
        onClick={() => setExpanded((e) => !e)}
        title={expanded ? "Collapse sidebar" : "Expand sidebar"}
        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        {expanded ? "‹" : "›"}
      </button>
    </nav>
  );
}
